import React, { useEffect } from 'react';
import { AlertCircle, CheckCircle, Info, X, Loader2 } from 'lucide-react';

interface ToastProps {
  id: string;
  type: 'success' | 'error' | 'info' | 'loading';
  message: string | React.ReactNode;
  onClose: (id: string) => void;
  duration?: number;
}

export const Toast: React.FC<ToastProps> = ({
  id,
  type,
  message,
  onClose,
  duration = 4000
}) => {
  useEffect(() => {
    if (type !== 'loading') {
      const timer = setTimeout(() => {
        onClose(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, duration, onClose, type]);

  const icons = {
    success: <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400" />,
    error: <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />,
    info: <Info className="w-5 h-5 text-blue-500 dark:text-blue-400" />,
    loading: <Loader2 className="w-5 h-5 text-blue-500 dark:text-blue-400 animate-spin" />
  };

  const borders = {
    success: 'border-green-200 dark:border-green-800',
    error: 'border-red-200 dark:border-red-800',
    info: 'border-blue-200 dark:border-blue-800',
    loading: 'border-blue-200 dark:border-blue-800'
  };

  return (
    <div
      className={`
        w-full pointer-events-auto overflow-hidden rounded-lg shadow-lg 
        border ${borders[type]} backdrop-blur-sm bg-white/60 dark:bg-gray-900/60
        transition-all duration-300 ease-in-out animate-toast-slide-right
      `}
      role="alert"
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {icons[type]}
          </div>
          <div className="ml-3 w-0 flex-1 pt-0.5">
            <div className="text-sm font-medium text-gray-900 dark:text-white whitespace-pre-line">
              {typeof message === 'string' ? (
                <span>{message}</span>
              ) : (
                message
              )}
            </div>
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              className="inline-flex text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 focus:outline-none"
              onClick={() => onClose(id)}
            >
              <span className="sr-only">Close</span>
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
